import * as React from "react";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Field, useFormikContext } from "formik";
import dayjs, { Dayjs } from "dayjs";
import { TextField } from "formik-mui";
import { useTranslation } from "react-i18next";

interface Props {
  name: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  helperText?: string;
  variant?: "outlined" | "filled" | "standard";
  validationRegex?: string;
  validationMessage?: string;
  size?: "small" | "medium" | "large";
  disableFuture?: boolean;
  disablePast?: boolean;
}

export const DATE_TIME_PICKER_FORMAT = "MM/DD/YYYY HH:mm";

const DateTimePickerField: React.FC<Props> = ({
  name,
  label,
  required,
  disabled,
  helperText,
  variant,
  validationRegex,
  validationMessage,
  size,
  disableFuture,
  disablePast
}) => {
  const { t } = useTranslation();
  const { setFieldValue, values } = useFormikContext<Record<string, string>>();
  const value = values[name] ? dayjs(values[name], DATE_TIME_PICKER_FORMAT) : null;

  const onChange = React.useCallback(
    (dayjsDate: Dayjs | null) => {
      setFieldValue(name, dayjsDate?.format(DATE_TIME_PICKER_FORMAT));
    },
    [name, setFieldValue]
  );

  const validate = React.useCallback(
    (fieldValue: string | null) => {
      if (required && !fieldValue) {
        return t("common.validation.required");
      }
      if (validationRegex && fieldValue && !fieldValue.match(new RegExp(validationRegex))) {
        return validationMessage || "Invalid format";
      }
      return undefined;
    },
    [required, validationRegex, t, validationMessage]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DateTimePicker<Dayjs>
        label={label}
        value={value}
        onChange={onChange}
        disabled={disabled}
        disableFuture={disableFuture}
        disablePast={disablePast}
        renderInput={({ error, ...other }) => {
          if (error) {
            window.console.log("Ignoring component's error value", error);
          }
          return (
            <Field
              {...other}
              name={name}
              component={TextField}
              validate={validate}
              required={required}
              helperText={helperText}
              variant={variant || "standard"}
              size={size}
            />
          );
        }}
      />
    </LocalizationProvider>
  );
};

export { DateTimePickerField };
